import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import CustomMultiSelect from '../../components/FormElements/MultiSelect';

const BulkContestModal = ({
  open,
  onClose,
  contests,
  onSubmit,
  selectedCount,
}) => {
  const [selectedContests, setSelectedContests] = useState([]);
  const [action, setAction] = useState('add');

  // Sort contests alphabetically by title
  const sortedContests = useMemo(() => {
    return [...contests].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
  }, [contests]);

  const handleContestChange = (selected) => {
    setSelectedContests(selected);
  };

  const handleActionChange = (event, newAction) => {
    if (newAction !== null) {
      setAction(newAction);
    }
  };

  const handleSubmit = () => {
    if (selectedContests.length > 0) {
      onSubmit(selectedContests, action);
    }
  };

  const handleClose = () => {
    setSelectedContests([]);
    setAction('add');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          width: '450px',
          maxWidth: '90vw',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Bulk Contest Assignment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedCount} audio(s) selected
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
            Action
          </Typography>
          <ToggleButtonGroup
            value={action}
            exclusive
            onChange={handleActionChange}
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 500,
                '&.Mui-selected': {
                  backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: action === 'add' ? '#43A047' : '#E53935',
                  },
                },
              },
            }}
          >
            <ToggleButton value="add">
              <Add sx={{ mr: 1 }} /> Add to Contests
            </ToggleButton>
            <ToggleButton value="remove">
              <Remove sx={{ mr: 1 }} /> Remove from Contests
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
            Select Contests to {action === 'add' ? 'Add' : 'Remove'}
          </Typography>
          <CustomMultiSelect
            selectOptions={sortedContests}
            onChange={handleContestChange}
            placeholder={`Select contests to ${action}`}
            value={selectedContests}
          />
        </Box>

        {selectedContests.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '12px', color: '#666', mb: 1 }}>
              Selected contests:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedContests.map((contestId) => {
                const contest = contests.find((c) => c._id === contestId);
                return contest ? (
                  <Chip
                    key={contestId}
                    avatar={<Avatar src={contest.logo} sx={{ width: 24, height: 24 }} />}
                    label={contest.title?.slice(0, 20) + (contest.title?.length > 20 ? '...' : '')}
                    size="small"
                    onDelete={() =>
                      setSelectedContests((prev) =>
                        prev.filter((id) => id !== contestId)
                      )
                    }
                    sx={{
                      backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': { color: 'white' },
                      },
                    }}
                  />
                ) : null;
              })}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: action === 'add' ? '#e8f5e9' : '#ffebee',
            borderRadius: '8px',
          }}
        >
          <Typography sx={{ fontSize: '12px', color: action === 'add' ? '#2e7d32' : '#c62828' }}>
            {action === 'add'
              ? 'Selected audios will be added to these contests. Existing contest assignments will be preserved.'
              : 'Selected audios will be removed from these contests. Other contest assignments will be preserved.'}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: '#666',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={selectedContests.length === 0}
          sx={{
            backgroundColor: action === 'add' ? '#4CAF50' : '#f44336',
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: action === 'add' ? '#43A047' : '#E53935',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#999',
            },
          }}
        >
          {action === 'add' ? 'Add to' : 'Remove from'} Contests
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkContestModal;
